import React, { useState } from 'react';
import { cn } from '../utils/cn';

export type DetectorMode = 'motion' | 'object' | 'yolo';

interface GoalSettingProps {
  onStartSession: (
    throwsGoal?: number,
    timeGoal?: number,
    calibrationTime?: number,
    mode?: DetectorMode
  ) => void | Promise<void>;
  defaultDetectorMode?: DetectorMode;
}

export const GoalSetting = ({
  onStartSession,
  defaultDetectorMode = 'object',
}: GoalSettingProps) => {
  const [throwsGoal, setThrowsGoal] = useState('');
  const [timeGoal, setTimeGoal] = useState('');
  const [calibrationTime, setCalibrationTime] = useState('3');
  const [detectorMode, setDetectorMode] = useState<DetectorMode>(defaultDetectorMode);

  const handleThrowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && !Number.isNaN(Number(value)))) {
      setThrowsGoal(value);
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && !Number.isNaN(Number(value)))) {
      setTimeGoal(value);
    }
  };

  const handleCalibrationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && Number(value) <= 10 && !Number.isNaN(Number(value)))) {
      setCalibrationTime(value);
    }
  };

  const isValid = throwsGoal !== '' || timeGoal !== '';

  const handleStart = () => {
    const throws = throwsGoal ? Number(throwsGoal) : undefined;
    const time = timeGoal ? Number(timeGoal) * 60 : undefined;
    const calibration = calibrationTime ? Number(calibrationTime) : 3;
    onStartSession(throws, time, calibration, detectorMode);
  };

  return (
    <div className="p-5 max-w-md mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 mb-5">Set Your Goals</h2>
      <div className="mb-5">
        <label htmlFor="throws-goal" className="block mb-1 text-gray-700">
          Consecutive Throws Goal (optional):
        </label>
        <input
          id="throws-goal"
          type="number"
          value={throwsGoal}
          onChange={handleThrowsChange}
          placeholder="e.g., 50"
          className="w-full p-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          min={1}
          aria-label="Consecutive throws goal"
        />
      </div>
      <div className="mb-5">
        <label htmlFor="time-goal" className="block mb-1 text-gray-700">
          Cumulative Time Goal in minutes (optional):
        </label>
        <input
          id="time-goal"
          type="number"
          value={timeGoal}
          onChange={handleTimeChange}
          placeholder="e.g., 10"
          className="w-full p-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          min={1}
          aria-label="Time goal in minutes"
        />
      </div>
      <div className="mb-5">
        <span className="block mb-2 text-gray-700">Detection mode:</span>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="detector-mode"
              checked={detectorMode === 'yolo'}
              onChange={() => setDetectorMode('yolo')}
              className="focus:ring-green-500"
            />
            <span>YOLO (custom ball model) — best accuracy</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="detector-mode"
              checked={detectorMode === 'object'}
              onChange={() => setDetectorMode('object')}
              className="focus:ring-green-500"
            />
            <span>Object (tracks round things) — demo fallback</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="detector-mode"
              checked={detectorMode === 'motion'}
              onChange={() => setDetectorMode('motion')}
              className="focus:ring-green-500"
            />
            <span>Motion (fallback)</span>
          </label>
        </div>
        <small className="text-gray-600 text-sm block mt-1">
          YOLO requires a trained model in public/models/. See JuggleVisionSpec.md.
        </small>
      </div>
      <div className="mb-5">
        <label htmlFor="calibration-time" className="block mb-1 text-gray-700">
          Calibration Time in seconds (1-10):
        </label>
        <input
          id="calibration-time"
          type="number"
          value={calibrationTime}
          onChange={handleCalibrationChange}
          placeholder="3"
          min={1}
          max={10}
          className="w-full p-2 text-base border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          aria-label="Calibration time in seconds"
        />
        <small className="text-gray-600 text-sm block mt-1">
          Time to get into position before tracking starts
        </small>
      </div>
      <button
        type="button"
        onClick={handleStart}
        disabled={!isValid}
        className={cn(
          'w-full py-3 text-lg rounded border-0 text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
          isValid ? 'cursor-pointer bg-green-600 hover:bg-green-700' : 'cursor-not-allowed bg-gray-400'
        )}
      >
        Start Session
      </button>
    </div>
  );
};
